import { createElement as rc, useState, useMemo } from 'react';
import PropTypes from 'prop-types';
import lodash from 'lodash';
import { View } from 'lib_ui-primitives';
import DropDownButton from '../_abstractComponent/DropDownButton';
import FormListSelection, { getSelectionRowId, _private as _p } from './FormListSelection';
import GridGl2025 from '../list/Grid';
import {useColumnWidths} from '../list/columns/index';
import {useColumnCustomization} from '../list/index';
import HeaderRow from '../list/TableList/HeaderRow';
import useRowDetail from '../list/TableList/useRowDetail';

export { _p };
export const _private = _p;
const EMPTY_ARRAY = [];

function ReadOnlyFormList(props) {
    const { hNode } = props;
    const { value } = _p.useFormControl(props);
    const [filterValue, setFilterValue] = useState(hNode.rowFilterDefaultValue || '');
    const rows = Array.isArray(value) ? value : EMPTY_ARRAY;
    const records = useMemo(
        () => hNode.rowFilterProperty && filterValue
            ? rows.filter(row => String(lodash.get(row, hNode.rowFilterProperty)) === filterValue)
            : rows,
        [rows, filterValue, hNode.rowFilterProperty]
    );
    const listProps = { ...props, records, rows };
    const grid = hNode.selectionProperty
        ? rc(FormListSelection, listProps, rc(FormListGrid, { ...listProps, hNode: { ...hNode, includeCheckbox: true } }))
        : rc(FormListGrid, listProps);
    if (!hNode.rowFilterProperty) return grid;

    const options = (hNode.rowFilterValues || []).map((value, index) => ({
        value,
        title: hNode.rowFilterLabels?.[index] || value
    }));
    options.push({ value: '', title: 'All' });
    const label = options.find(option => option.value === filterValue)?.title || filterValue;
    // Filtering changes only the visible rows; approval stays in selectionProperty.
    // prettier-ignore
    return rc(View, { style: { flexDirection: 'column', flexGrow: 1, flexBasis: '100%', width: '100%', minHeight: 0 } },
        rc(View, { style: { flexDirection: 'row', flexShrink: 0 } },
            rc(DropDownButton, { id: `${hNode.id}-filter`, title: `Show: ${label}`, value: `Show: ${label}`, icon: 'chevron-down', buttonStyle: 'Secondary' },
                options.map(option => rc(DropDownButton.MenuItem, {
                    key: option.value,
                    hNode: { title: option.title },
                    onClick: () => setFilterValue(option.value)
                }))
            )
        ),
        grid
    );
}

function FormListGrid(props) {
    const {
        hNode = {}
    } = props;
    const records = useMemo(
        () => hNode.selectionKeys?.length
        ? props.records.map(row => ({ ...row, _id: getSelectionRowId(row, hNode.selectionKeys) }))
        : props.records,
        [props.records, hNode.selectionKeys]
    );

    const noop = () => {};

    // Get the width of each column from the column definition
    const {columnWidths, handleColumnWidthChange} = useColumnWidths(props, records);
    // Pass the resulting column nodes with the widths to column customization
    const [columnHNodes] = useColumnCustomization(props, columnWidths);
    const [RowDetail, onScrollLayoutChange] = useRowDetail(hNode, columnHNodes, noop, '');

    return rc(GridGl2025, {
        hNode,
        listType: 'table',
        records,
        recordCount: records.length,
        sortedColumns: {},
        columnHNodes,
        columnWidths,
        headerClicked: noop,
        onClick: noop,
        onRenderItem: noop,
        RowDetail,
        HeaderRow,
        onScrollLayoutChange,
        style: {
            height: '100%',
            ...(hNode.selectionProperty ? { minHeight: 240 } : {}),
            flexBasis: '100%'
        },
        endOfRecordsText: '',
        handleColumnWidthChange
    });

}

ReadOnlyFormList.propTypes = {
    hNode: PropTypes.shape({
        treePosition: PropTypes.shape({
            sequence: PropTypes.number.isRequired
        }).isRequired
    }).isRequired
};

export default ReadOnlyFormList;
